import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Button } from '@mui/material';
import { styled } from '@mui/system';
import { isEmpty } from 'lodash';
import { init } from 'echarts';
import { round } from 'mathjs';

import Color from '@tool/color';
import { newRate } from '@tool/index';

interface IProps {
  height: number,
  idx: number,
  index: number,
  xData: any[],
  yData: any[],
}
interface IState {}

const Chart = (props: IProps, state: IState) => {
  const navi = useNavigate();
  let chart: any = null;
  const chart10Ref = useRef<any>(null);
  const colorList = [Color.cf, Color.secondary, Color.primary];
  const { xData, yData, height, idx, index } = props;

  const newValue = (value: any, digit: number = 1) => {
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(value)) return '-';
    if (value === null || value === '' || value === undefined) return '-';
    return `${round(value * 100, 0)}%`;
  };

  const render = () => {
    if (chart) chart.dispose();
    chart = init(chart10Ref.current);

    const series: any[] = [];
    yData.map((item: any) => {
      let newData: any[] = [];
      if (index === 0) newData = item.data.slice(0, 5);
      else if (index === 1) newData = item.data.slice(5 * idx + 5 * (idx - 1), 5 * idx + 5 * (idx - 1) + 5);
      else if (index === 2) newData = item.data.slice(5 * idx + 5 + 5 * (idx - 1), 5 * idx + 5 * (idx - 1) + 10);

      series.push({
        name: item.name,
        data: newData,
        type: 'bar',
        barWidth: '10px',
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
        },
      });
    });

    const option = {
      color: colorList,
      legend: {
        bottom: 10,
        itemWidth: 8,
        itemHeight: 8,
      },
      grid: {
        left: 10,
        right: 10,
        bottom: 40,
        top: 20,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: {
          show: true,
          lineStyle: { color: Color.ccc },
        },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          fontSize: 10,
          color: Color.blackColor(0.85),
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'value',
        axisTick: { show: false },
        axisLabel: {
          show: true,
          formatter: (val: number) => {
            if (val === 0) return 0;
            return `${newValue(val, 1)}`;
          },
          fontSize: 12,
          color: Color.blackColor(0.5),
          fontFamily: 'Roboto',
        },
        axisLine: {
          lineStyle: { color: Color.da },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: Color.primaryColor(0.08),
            width: 1,
            type: 'dashed',
          },
        },
      },
      series: series,
    };

    chart.setOption(option, true);
  };

  useEffect(() => {
    render();
    window.addEventListener('resize', render);

    return () => {
      window.removeEventListener('resize', render);
    };
  }, [xData, yData]);

  return (
    <section ref={chart10Ref}
      style={{
        width: '100%',
        height: `${height}px`,
      }} />
  );
};

export default observer(Chart);
